import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { searchPublicExercises, getVideoRanksForExercises } from '../services/exerciseService'
import { getActionableWeakPoints, collectAutoTagsFromExercises, scoreExerciseForWeakPoints } from '../utils/studentProgressInsights'
import ExerciseCard from './ExerciseCard'
import './ExerciseSuggestions.css'

const SUGGESTIONS_COUNT = 3
const RECENT_EXERCISES_LIMIT = 20

function ExerciseSuggestions({
  profileStats,
  attempts,
  onSwitchTab,
  onPillClick,
  onOpenCodex,
  allObjectivesCompleted,
  publicTeaserOnly = false,
  title = 'Exercices recommandés',
  subtitle = null,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [suggestions, setSuggestions] = useState([])
  const [isPersonalized, setIsPersonalized] = useState(true)
  const [noPublicExercises, setNoPublicExercises] = useState(false)
  const [isReprise, setIsReprise] = useState(false)
  const [loading, setLoading] = useState(true)
  const [videoRanksMap, setVideoRanksMap] = useState(() => new Map())

  useEffect(() => {
    loadSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSuggestions dépend de generateSuggestions (stable par closure)
  }, [profileStats, attempts, publicTeaserOnly])

  useEffect(() => {
    if (!suggestions.length) {
      setVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(suggestions).then(setVideoRanksMap).catch(() => setVideoRanksMap(new Map()))
  }, [suggestions])

  const loadSuggestions = async () => {
    try {
      setLoading(true)
      const allAttempts = attempts || []
      const result = await generateSuggestions(profileStats || null, allAttempts)
      setSuggestions(result.suggestions)
      setIsPersonalized(result.isPersonalized)
      setNoPublicExercises(result.noPublicExercises)
      setIsReprise(result.isReprise ?? false)
    } catch (error) {
      console.error('Erreur lors du chargement des suggestions:', error)
      setSuggestions([])
      setNoPublicExercises(false)
      setIsReprise(false)
    } finally {
      setLoading(false)
    }
  }

  const generateSuggestions = async (stats, allAttempts) => {
    const recentExerciseIds = allAttempts
      .slice(0, RECENT_EXERCISES_LIMIT)
      .map(a => a.exerciseId)
      .filter(Boolean)

    const allExercises = await searchPublicExercises({
      excludeHorizons: true,
      ...(publicTeaserOnly ? { publicTeaserOnly: true } : {}),
    })
    if (allExercises.length === 0) {
      return { suggestions: [], isPersonalized: false, noPublicExercises: true }
    }

    const weakPoints = stats
      ? getActionableWeakPoints(stats, allAttempts, collectAutoTagsFromExercises(allExercises), {
          max: 3
        })
      : []
    const scoreFn = (ex) =>
      scoreExerciseForWeakPoints(ex, weakPoints, stats || {}, allAttempts, { primaryIndex: 0 })

    const candidates = allExercises.filter(ex => !recentExerciseIds.includes(ex.id))
    const scored = candidates.map(ex => ({ ...ex, score: scoreFn(ex) }))
    const withScore = weakPoints.length > 0
      ? scored.filter(ex => ex.score > 0)
      : scored
    const personalized = [...withScore]
      .sort((a, b) => b.score - a.score)
      .slice(0, SUGGESTIONS_COUNT)

    if (personalized.length > 0) {
      return { suggestions: personalized, isPersonalized: true, noPublicExercises: false }
    }

    const fallback = [...allExercises]
      .filter(ex => !recentExerciseIds.includes(ex.id))
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0)
        const dateB = b.createdAt?.toDate?.() || new Date(0)
        return dateB - dateA
      })
      .slice(0, SUGGESTIONS_COUNT)

    if (fallback.length > 0) {
      return { suggestions: fallback, isPersonalized: false, noPublicExercises: false }
    }

    const alreadyDone = allExercises.filter(ex => recentExerciseIds.includes(ex.id))
    const alreadyDoneScored = alreadyDone
      .map(ex => ({ ...ex, score: scoreFn(ex) }))
      .filter((ex) => weakPoints.length === 0 || ex.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, SUGGESTIONS_COUNT)
    return {
      suggestions: alreadyDoneScored,
      isPersonalized: alreadyDoneScored.length > 0 && weakPoints.length > 0,
      noPublicExercises: false,
      isReprise: alreadyDoneScored.length > 0
    }
  }

  const handleStartExercise = (exerciseId) => {
    navigate(`/play/${exerciseId}`, { state: { returnTo: `${location.pathname}${location.search || ''}` } })
  }

  if (loading) {
    return (
      <div className="suggestions-loading">
        <div className="spinner-small"></div>
      </div>
    )
  }

  if (noPublicExercises) {
    return (
      <div className="exercise-suggestions exercise-suggestions-no-public">
        <h3 className="suggestions-title">{title}</h3>
        {subtitle ? <p className="suggestions-subtitle">{subtitle}</p> : null}
        <div className="suggestions-empty suggestions-no-public">
          <p className="suggestions-empty-text">Aucun exercice public pour le moment</p>
          <p className="suggestions-empty-hint">Explore le Parcours ou le Mode libre pour t&apos;entraîner.</p>
          <div className="suggestions-cta-row">
            <button
              type="button"
              className="suggestion-button"
              onClick={() => onSwitchTab && onSwitchTab('campaign')}
            >
              Parcours
            </button>
            <button
              type="button"
              className="suggestion-button suggestion-button-secondary"
              onClick={() => onSwitchTab && onSwitchTab('free-mode')}
            >
              Mode libre
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (suggestions.length === 0) {
    return (
      <div className="exercise-suggestions">
        <h3 className="suggestions-title">{title}</h3>
        <div className="suggestions-empty">
          <p className="suggestions-empty-text">Aucune suggestion pour le moment</p>
        </div>
      </div>
    )
  }

  const sectionTitle = title || (isReprise ? 'À refaire' : (isPersonalized ? 'Recommandé pour toi' : 'À découvrir'))
  const getBadgeLabel = (index) => {
    if (isReprise) return 'Déjà fait – à revoir'
    if (allObjectivesCompleted && index === 0) return 'Prioritaire'
    if (isPersonalized) return 'Recommandé'
    return 'À explorer'
  }

  return (
    <div className="exercise-suggestions" id="suivi-suggestions">
      <h3 className="suggestions-title">{sectionTitle}</h3>
      {subtitle ? <p className="suggestions-subtitle">{subtitle}</p> : null}
      <div className="suggestions-list">
        {suggestions.map((exercise, index) => {
          const vr = videoRanksMap.get(exercise.id)
          return (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            allExercises={suggestions}
            videoRank={vr?.rank}
            videoGroupSize={vr?.groupSize}
            showDemoTeaserBadge={publicTeaserOnly}
            onClick={handleStartExercise}
            onPillClick={onPillClick}
            onOpenCodex={onOpenCodex}
            recommendedLabel={getBadgeLabel(index)}
            recommendedPillPayload={isReprise ? { type: 'doneStatus', value: 'done' } : undefined}
            variant="mini"
          />
        )})}
      </div>
    </div>
  )
}

export default ExerciseSuggestions
